import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
} from "@chakra-ui/react";
import { unixNow } from "applesauce-core/helpers";
import { NostrEvent } from "nostr-tools";
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import { EmbedEventCard } from "../../components/embed-event/card";
import { usePublishEvent } from "../global/publish-provider";

const REPORT_REASONS = [
  { value: "spam", label: "Spam" },
  { value: "nudity", label: "Nudity" },
  { value: "profanity", label: "Profanity" },
  { value: "illegal", label: "Illegal content" },
  { value: "impersonation", label: "Impersonation" },
  { value: "other", label: "Other" },
] as const;

type ReportModalContextType = {
  openModal: (event: NostrEvent) => void;
};

const ReportModalContext = createContext<ReportModalContextType>({
  openModal: () => {},
});

export function useReportModalContext() {
  return useContext(ReportModalContext);
}

function ReportModal({ event, onClose }: { event: NostrEvent; onClose: () => void }) {
  const publish = usePublishEvent();
  const [reason, setReason] = useState("spam");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const draft = {
        kind: 1984,
        content,
        tags: [
          ["e", event.id, "", reason],
          ["p", event.pubkey, "", reason],
        ],
        created_at: unixNow(),
      };
      await publish("Report", draft);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader px="4" py="2">
          Report Event
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px="4" py="0" display="flex" flexDirection="column" gap="2">
          <EmbedEventCard event={event} />
          <Select value={reason} onChange={(e) => setReason(e.target.value)}>
            {REPORT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Additional details (optional)"
            rows={3}
          />
        </ModalBody>
        <ModalFooter px="4" pb="4" pt="2">
          <Button variant="ghost" size="sm" mr={2} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="red" size="sm" onClick={handleSubmit} isLoading={loading}>
            Report
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function ReportModalProvider({ children }: PropsWithChildren) {
  const [reportEvent, setReportEvent] = useState<NostrEvent | null>(null);

  const openModal = useCallback((event: NostrEvent) => {
    setReportEvent(event);
  }, []);

  const context = useMemo(() => ({ openModal }), [openModal]);

  return (
    <ReportModalContext.Provider value={context}>
      {children}
      {reportEvent && <ReportModal event={reportEvent} onClose={() => setReportEvent(null)} />}
    </ReportModalContext.Provider>
  );
}
